/**
 * Semantic normalization for diff comparisons.
 *
 * Strips non-semantic OOXML metadata from node JSON before diffing so that
 * volatile attributes (regenerated by Word on every save, or assigned per
 * editor session) do not produce false-positive diffs or fingerprint
 * mismatches. This module is used exclusively by the diffing pipeline — it
 * never mutates live ProseMirror nodes or touches importer/exporter code.
 */

import { NON_SEMANTIC_BLOCK_ATTRS } from './identity-attrs';

/**
 * Keys inside `originalAttributes` on image nodes that Word regenerates
 * on every save. These are drawing-level identifiers, not content.
 */
const VOLATILE_IMAGE_ORIGINAL_ATTR_KEYS = new Set(['wp14:anchorId', 'wp14:editId']);

/**
 * Removes volatile keys from a flat attributes object.
 * Returns a new object — never mutates the input.
 */
function omitKeys(attrs: Record<string, unknown>, keysToOmit: Set<string>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(attrs)) {
    if (!keysToOmit.has(key)) {
      result[key] = value;
    }
  }
  return result;
}

/**
 * Strips volatile OOXML metadata from paragraph attributes.
 *
 * @param attrs Raw paragraph node attributes.
 * @returns A shallow copy with non-semantic keys removed.
 */
export function normalizeParagraphAttrs(attrs: Record<string, unknown>): Record<string, unknown> {
  return omitKeys(attrs, NON_SEMANTIC_BLOCK_ATTRS);
}

/**
 * Strips identity-only block attributes (`sdBlockId`, `sdBlockRev`, OOXML
 * `rsid*` family) from any block node's attrs. Used by `normalizeDocNodeJSON`
 * to keep the fingerprint stable across editor instances.
 *
 * @param attrs Raw node attributes.
 * @returns A shallow copy with non-semantic block keys removed.
 */
function normalizeBlockAttrs(attrs: Record<string, unknown>): Record<string, unknown> {
  return omitKeys(attrs, NON_SEMANTIC_BLOCK_ATTRS);
}

/**
 * Strips volatile OOXML metadata from an image node's JSON representation.
 *
 * Only touches `attrs.originalAttributes` — all other attributes are
 * preserved as-is so that genuine image changes (src, size, wrapping, etc.)
 * still produce diffs.
 *
 * @param nodeJSON Serialized image node (from `node.toJSON()`).
 * @returns A deep-enough copy with volatile keys removed from `originalAttributes`.
 */
export function normalizeImageNodeJSON(nodeJSON: Record<string, unknown>): Record<string, unknown> {
  const attrs = nodeJSON.attrs as Record<string, unknown> | undefined;
  if (!attrs?.originalAttributes) {
    return nodeJSON;
  }

  const originalAttributes = attrs.originalAttributes as Record<string, unknown>;
  const cleanedOriginalAttributes = omitKeys(originalAttributes, VOLATILE_IMAGE_ORIGINAL_ATTR_KEYS);

  return {
    ...nodeJSON,
    attrs: {
      ...attrs,
      originalAttributes: cleanedOriginalAttributes,
    },
  };
}

/**
 * Strips volatile metadata from an inline node's JSON based on its type.
 *
 * Currently normalizes image nodes. Other inline node types pass through
 * unchanged — extend the switch as new volatile-attr patterns emerge.
 *
 * @param nodeJSON Serialized inline node.
 * @returns Normalized copy (or the original if no normalization is needed).
 */
export function normalizeInlineNodeJSON(nodeJSON: Record<string, unknown>): Record<string, unknown> {
  if (nodeJSON.type === 'image') {
    return normalizeImageNodeJSON(nodeJSON);
  }
  return nodeJSON;
}

/**
 * Strips volatile metadata from an inline node's raw attributes.
 *
 * Used when computing attrsDiff for modified inline nodes so that
 * volatile keys don't appear in the diff payload.
 *
 * @param typeName The node type name (e.g. 'image').
 * @param attrs Raw node attributes.
 * @returns Normalized copy with volatile keys removed.
 */
export function normalizeInlineNodeAttrs(typeName: string, attrs: Record<string, unknown>): Record<string, unknown> {
  if (typeName !== 'image') {
    return attrs;
  }

  const originalAttributes = attrs.originalAttributes as Record<string, unknown> | undefined;
  if (!originalAttributes) {
    return attrs;
  }

  return {
    ...attrs,
    originalAttributes: omitKeys(originalAttributes, VOLATILE_IMAGE_ORIGINAL_ATTR_KEYS),
  };
}

/**
 * Produces a stable semantic key for an inline node, suitable for
 * identity comparison in Myers diff.
 *
 * Two nodes with the same key represent the same semantic content.
 * Volatile OOXML metadata is stripped so that re-imported copies of
 * the same image are treated as identical.
 *
 * @param node A ProseMirror node (or mock) with `type.name` and `toJSON()`.
 * @returns Stable JSON string usable as a comparison key.
 */
export function semanticInlineNodeKey(node: { type: { name: string }; toJSON: () => unknown }): string {
  return JSON.stringify(normalizeInlineNodeJSON(node.toJSON() as Record<string, unknown>));
}

/**
 * Strips volatile metadata from a paragraph node's full JSON representation.
 *
 * Normalizes the paragraph's own attrs and recursively normalizes any
 * inline image nodes nested within its content tree.
 *
 * @param nodeJSON Serialized paragraph node (from `node.toJSON()`).
 * @returns Normalized copy suitable for semantic comparison.
 */
export function normalizeParagraphNodeJSON(nodeJSON: Record<string, unknown>): Record<string, unknown> {
  const attrs = (nodeJSON.attrs as Record<string, unknown>) ?? {};
  const content = nodeJSON.content as Record<string, unknown>[] | undefined;

  return {
    ...nodeJSON,
    attrs: normalizeParagraphAttrs(attrs),
    ...(content ? { content: content.map(normalizeContentNodeJSON) } : {}),
  };
}

/**
 * Recursively normalizes a content node within a paragraph's JSON tree.
 *
 * Applies inline node normalization to leaf nodes (e.g. images) and
 * recurses into container nodes (e.g. runs) that have their own content.
 */
function normalizeContentNodeJSON(nodeJSON: Record<string, unknown>): Record<string, unknown> {
  const content = nodeJSON.content as Record<string, unknown>[] | undefined;

  // Leaf inline nodes (image, etc.)
  if (!content) {
    return normalizeInlineNodeJSON(nodeJSON);
  }

  // Container nodes (run, etc.) — recurse into children
  return {
    ...nodeJSON,
    content: content.map(normalizeContentNodeJSON),
  };
}

/**
 * Normalizes an entire document JSON tree for diff fingerprinting.
 *
 * Walks the full document structure, stripping volatile paragraph and
 * image attributes at every level. Used by `canonicalize.ts` to ensure
 * fingerprints and diff comparisons agree on what counts as a real change.
 *
 * @param docJSON Serialized document (from `doc.toJSON()`).
 * @returns Normalized copy suitable for stable fingerprinting.
 */
export function normalizeDocJSON(docJSON: Record<string, unknown>): Record<string, unknown> {
  const content = docJSON.content as Record<string, unknown>[] | undefined;
  if (!content) {
    return docJSON;
  }

  return {
    ...docJSON,
    content: content.map(normalizeDocNodeJSON),
  };
}

/**
 * Normalizes a single node within the document tree based on its type.
 *
 * Strips identity-only block attrs (`sdBlockId`, `sdBlockRev`, etc.) from
 * every block node — not just paragraphs — so two editor instances loaded
 * from the same DOCX produce the same fingerprint.
 */
function normalizeDocNodeJSON(nodeJSON: Record<string, unknown>): Record<string, unknown> {
  const type = nodeJSON.type as string | undefined;

  if (type === 'paragraph') {
    return normalizeParagraphNodeJSON(nodeJSON);
  }

  // Recurse into structural containers (body, section, table, row, cell, etc.)
  // and strip identity attrs from each container's own attrs as well.
  const attrs = nodeJSON.attrs as Record<string, unknown> | undefined;
  const content = nodeJSON.content as Record<string, unknown>[] | undefined;
  const normalizedAttrs = attrs ? normalizeBlockAttrs(attrs) : undefined;

  if (content) {
    return {
      ...nodeJSON,
      ...(normalizedAttrs ? { attrs: normalizedAttrs } : {}),
      content: content.map(normalizeDocNodeJSON),
    };
  }

  if (normalizedAttrs) {
    return { ...nodeJSON, attrs: normalizedAttrs };
  }
  return nodeJSON;
}

// ---------------------------------------------------------------------------
// Legacy normalization (SD-3279 backward compatibility)
//
// Reproduces the pre-SD-3279 normalization exactly. Used only by the
// validation fallback path in `diff-service.ts` to accept snapshot and diff
// payloads captured under the old algorithm. The difference from the current
// algorithm: paragraph-only attribute stripping over a smaller set (no
// `sdBlockId` / `sdBlockRev`), no attribute stripping on structural
// containers. Image originalAttributes stripping is unchanged across both
// algorithms and is reused.
// ---------------------------------------------------------------------------

const LEGACY_VOLATILE_PARAGRAPH_ATTRS = new Set<string>([
  'paraId',
  'textId',
  'rsidR',
  'rsidRDefault',
  'rsidP',
  'rsidRPr',
  'rsidDel',
]);

function normalizeParagraphAttrsLegacy(attrs: Record<string, unknown>): Record<string, unknown> {
  return omitKeys(attrs, LEGACY_VOLATILE_PARAGRAPH_ATTRS);
}

function normalizeParagraphNodeJSONLegacy(nodeJSON: Record<string, unknown>): Record<string, unknown> {
  const attrs = (nodeJSON.attrs as Record<string, unknown>) ?? {};
  const content = nodeJSON.content as Record<string, unknown>[] | undefined;

  return {
    ...nodeJSON,
    attrs: normalizeParagraphAttrsLegacy(attrs),
    ...(content ? { content: content.map(normalizeContentNodeJSON) } : {}),
  };
}

function normalizeDocNodeJSONLegacy(nodeJSON: Record<string, unknown>): Record<string, unknown> {
  const type = nodeJSON.type as string | undefined;

  if (type === 'paragraph') {
    return normalizeParagraphNodeJSONLegacy(nodeJSON);
  }

  // Pre-SD-3279: structural containers passed through with attrs untouched;
  // only their content was recursed.
  const content = nodeJSON.content as Record<string, unknown>[] | undefined;
  if (content) {
    return {
      ...nodeJSON,
      content: content.map(normalizeDocNodeJSONLegacy),
    };
  }

  return nodeJSON;
}

/**
 * Reproduces the pre-SD-3279 document normalization for legacy fingerprint
 * validation. Used by `diff-service.ts` as a fallback when a snapshot or diff
 * payload was captured under the old algorithm and its stored fingerprint
 * would not match the current normalizer.
 *
 * @param docJSON Serialized document (from `doc.toJSON()`).
 * @returns Normalized copy matching the pre-SD-3279 algorithm.
 */
export function normalizeDocJSONLegacy(docJSON: Record<string, unknown>): Record<string, unknown> {
  const content = docJSON.content as Record<string, unknown>[] | undefined;
  if (!content) {
    return docJSON;
  }

  return {
    ...docJSON,
    content: content.map(normalizeDocNodeJSONLegacy),
  };
}
